import React, { memo, useMemo, useEffect } from 'react';
import cn from 'classnames';
import useSWRInfinite from 'swr/infinite';
import queryString from 'query-string';
import { useRecoilValue } from 'recoil';

import { useTranslation } from 'next-i18next';
import { Row } from 'components/layout';
import TouchpointCard from 'components/candidate/TouchpointCard';
import { Button } from 'components/form';
import { MatchCardsSkeleton } from 'components/skeletons/dashboard.component';
import { httpHeadersState } from 'lib/atoms/userSecretAtom';
import { get } from 'lib/utils/http';
import { API_ROUTES } from 'lib/api-routes';
import {
  parseArrayResponse,
  parsePaginatedResponse,
  parseResponse,
} from 'lib/utils/parser';

import { TouchpointType } from 'lib/models/touchpoint';
import { CompanyAttributes } from 'lib/models/company';
import { StudentSocietyAttributes } from 'lib/models/student-society';
import { MatchAttributes, MatchResponse } from 'lib/models/matches';
import { OpportunityAttributes } from 'lib/models/opportunity';
import { getOpportunityPublicPagePathname } from 'utils/urls';

import styles from '../Section.module.scss';

const PER_PAGE_COUNT = 5;

export interface PastMatchesProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
}

export const PastMatches: React.FC<PastMatchesProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
  }: PastMatchesProps) => {
    const { headers } = useRecoilValue(httpHeadersState);
    const [t] = useTranslation('candidate-dashboard');

    const endOfPageRef = React.useRef<HTMLDivElement | null>(null);
    const scrollToBottom = () => {
      endOfPageRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    };

    const getTouchpointListKey = (pageIndex: number) => {
      const params = {
        page: pageIndex + 1,
        per_page: PER_PAGE_COUNT,
        with_details: true,
      };
      const qs = queryString.stringify(params, {
        arrayFormat: 'bracket',
        skipEmptyString: true,
      });
      const pathname = `${API_ROUTES.PAST_MATCHES}?${qs}`;

      return [pathname, headers];
    };

    const {
      data: touchpointsResponse,
      error: touchpointsError,
      size,
      setSize,
    } = useSWRInfinite<MatchResponse>(getTouchpointListKey, get, {
      revalidateOnFocus: false,
      revalidateFirstPage: false,
    });

    const isAPILoading = !touchpointsResponse && !touchpointsError;
    const isLoadingMore =
      isAPILoading ||
      (size > 0 &&
        touchpointsResponse &&
        typeof touchpointsResponse[size - 1] === 'undefined');

    const touchpoints = useMemo(() => {
      return parsePaginatedResponse<MatchAttributes>(touchpointsResponse);
    }, [touchpointsResponse]);

    const totalCount = touchpointsResponse
      ? touchpointsResponse[0]?.meta?.total
      : undefined;

    const hasMoreRecords = useMemo(() => {
      const firstApplicationList = Array.isArray(touchpointsResponse)
        ? touchpointsResponse[0]
        : null;
      if (firstApplicationList) {
        return (firstApplicationList?.meta?.total || 0) > size * PER_PAGE_COUNT;
      } else {
        return false;
      }
    }, [touchpointsResponse, size]);

    useEffect(() => {
      if (!isLoadingMore && size > 1) {
        scrollToBottom();
      }
    }, [isLoadingMore, size]);

    useEffect(() => {
      setSize(1);

      return () => {
        endOfPageRef.current = null;
        setSize(1);
      };
    }, []);

    const getTouchpointCard = (touchpoints: Array<MatchAttributes>) => {
      return (
        <div className={styles.touchpointcardList}>
          {touchpoints?.map(
            ({
              id,
              title,
              start_date,
              creatable_for,
              nested_type,
              minimum_salary,
              maximum_salary,
              matchable_slug,
              application_deadline,
              apply_url,
              pay_currency,
              kind,
              cities,
              matchable_slug: slug,
              cohost_applications: cohostApplicationsResponse,
              touchpoint,
            }) => {
              const createdBy = parseResponse<
                CompanyAttributes | StudentSocietyAttributes
              >(creatable_for);
              const opportunity =
                parseResponse<OpportunityAttributes>(touchpoint);
              const { name, icon_url, is_verified, huzzle_verified } =
                createdBy;

              const publicPageUrl = `${
                window.location.origin
              }${getOpportunityPublicPagePathname(
                nested_type,
                matchable_slug
              )}`;
              const cohost_applications = parseArrayResponse(
                cohostApplicationsResponse
              );
              return (
                <TouchpointCard
                  key={id}
                  icon_url={icon_url}
                  title={title}
                  createdBy={name}
                  startDate={start_date}
                  is_eligible_for_auto_apply={
                    opportunity.is_eligible_for_auto_apply
                  }
                  created_at={''}
                  application_deadline={application_deadline}
                  is_verified={is_verified}
                  touchpointable_type={nested_type as TouchpointType}
                  maximum_salary={maximum_salary}
                  minimum_salary={minimum_salary}
                  pay_currency={pay_currency || 'GBP'}
                  isExpired={false}
                  url={publicPageUrl}
                  className={styles.touchpointcard}
                  isEasyApply={
                    nested_type !== TouchpointType.Event && !apply_url
                  }
                  isNewSet={false}
                  huzzle_verified={huzzle_verified}
                  kind={kind}
                  isOnboarding
                  cities={cities}
                  id={id}
                  slug={slug}
                  cohost_applications={cohost_applications}
                  following={null}
                  apply_url={null}
                  extension_installed={true}
                  showManagerProgress={false}
                />
              );
            }
          )}
        </div>
      );
    };

    const contentClassNames = cn(
      {
        [styles.container]: true,
      },
      { [styles.section]: totalCount !== 0 },
      { [styles.hide]: totalCount === 0 },
      className
    );

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid="PastMatches"
        ref={endOfPageRef}
        id="pastMatches"
      >
        <p className={styles.title}>{t('recently-viewed-matches')}</p>
        {totalCount ? getTouchpointCard(touchpoints) : null}
        {totalCount === undefined || isLoadingMore ? (
          <MatchCardsSkeleton />
        ) : null}
        <Row align="center" columnGap={8} className={styles.countsRow}>
          {touchpoints?.length ? (
            <p>
              Showing {touchpoints?.length} of {totalCount}
            </p>
          ) : null}
          {hasMoreRecords ? (
            <Button
              variant="text"
              color="blue"
              onClick={() => {
                setSize(size + 1);
              }}
              className={styles.countsButton}
            >
              Show more
            </Button>
          ) : null}
        </Row>
      </div>
    );
  }
);

PastMatches.displayName = 'PastMatches';
